import { useState, useEffect, useCallback } from "react";
import { message } from "antd";
import roomPackageService from "../services/roomPackageService";
import type { RoomPackage } from "../types";

export function useRoomPackages() {
  const [packages, setPackages] = useState<RoomPackage[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const res = await roomPackageService.getAll();
      if (res.data) {
        setPackages(res.data);
      }
    } catch {
      message.error("Failed to fetch room packages");
    } finally {
      setLoading(false);
    }
  }, []);

  const create = useCallback(async (data: Partial<RoomPackage> & { inclusions?: any[] }) => {
    try {
      const res = await roomPackageService.create(data);
      if (res.data) {
        message.success("Room package created");
        await fetchAll();
        return res.data;
      }
    } catch {
      message.error("Failed to create room package");
    }
    return null;
  }, [fetchAll]);

  const update = useCallback(async (id: string, data: Partial<RoomPackage> & { inclusions?: any[] }) => {
    try {
      const res = await roomPackageService.update(id, data);
      if (res.data) {
        message.success("Room package updated");
        await fetchAll();
        return res.data;
      }
    } catch {
      message.error("Failed to update room package");
    }
    return null;
  }, [fetchAll]);

  const remove = useCallback(async (id: string) => {
    try {
      await roomPackageService.delete(id);
      message.success("Room package deleted");
      await fetchAll();
      return true;
    } catch {
      message.error("Failed to delete room package");
    }
    return false;
  }, [fetchAll]);

  useEffect(() => {
    void fetchAll();
  }, [fetchAll]);

  return { packages, loading, fetchAll, create, update, remove };
}
